import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowRight, Mail, Lock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SignupSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = searchParams.get('session_id');
    
    const [status, setStatus] = useState('checking'); // checking, success, failed
    const [paymentData, setPaymentData] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const maxAttempts = 10;
    
    useEffect(() => {
        if (sessionId) {
            pollPaymentStatus();
        } else {
            setStatus('failed');
        }
    }, [sessionId]);
    
    const pollPaymentStatus = async () => {
        if (attempts >= maxAttempts) {
            setStatus('failed');
            return;
        }
        
        try {
            const res = await axios.get(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
            
            if (res.data.payment_status === 'paid') {
                setStatus('success');
                setPaymentData(res.data);
            } else if (res.data.status === 'expired') {
                setStatus('failed');
            } else {
                // Continue polling
                setAttempts(prev => prev + 1);
                setTimeout(pollPaymentStatus, 2000);
            }
        } catch (err) {
            console.error('Error checking payment status:', err);
            setAttempts(prev => prev + 1);
            setTimeout(pollPaymentStatus, 2000);
        }
    };
    
    if (status === 'checking') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 text-teal-500 animate-spin mx-auto mb-6" />
                    <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment...</h2>
                    <p className="text-slate-400">Please wait while we confirm your payment.</p>
                </div>
            </div>
        );
    }
    
    if (status === 'failed') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-slate-800 rounded-2xl p-8 text-center border border-slate-700">
                    <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <XCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Payment Failed</h2>
                    <p className="text-slate-400 mb-6">
                        We couldn't verify your payment. Please try again or contact support.
                    </p>
                    <button
                        onClick={() => navigate('/signup')}
                        className="px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
            <div className="max-w-lg w-full">
                {/* Success Card */}
                <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700 mb-6">
                    <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-emerald-500" />
                    </div>
                    
                    <h1 className="text-3xl font-bold text-white mb-2">Welcome to Shipez.ai!</h1>
                    <p className="text-slate-400 mb-6">
                        Your account has been created and payment was successful.
                    </p>
                    
                    {paymentData && (
                        <div className="bg-slate-700/30 rounded-xl p-4 mb-6 text-left">
                            <div className="flex justify-between mb-2">
                                <span className="text-slate-400 text-sm">Company</span>
                                <span className="text-white text-sm font-medium">{paymentData.metadata?.company_name}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <span className="text-slate-400 text-sm">Modules</span>
                                <span className="text-white text-sm font-medium">
                                    {paymentData.metadata?.module_codes?.split(',').length || 0} activated
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-400 text-sm">Amount Paid</span>
                                <span className="text-emerald-400 text-sm font-bold">
                                    ${paymentData.amount?.toFixed(2)} {paymentData.currency?.toUpperCase()}
                                </span>
                            </div>
                        </div>
                    )}
                    
                    {/* Next Steps */}
                    <div className="text-left space-y-3 mb-6">
                        <h3 className="text-sm font-semibold text-teal-400 uppercase">Next Steps</h3>
                        <div className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                            <Mail className="w-5 h-5 text-teal-500 mt-0.5" />
                            <div>
                                <p className="text-white text-sm font-medium">Check your email</p>
                                <p className="text-slate-400 text-xs">We've sent login details to your admin email</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                            <Lock className="w-5 h-5 text-teal-500 mt-0.5" />
                            <div>
                                <p className="text-white text-sm font-medium">Sign in to your account</p>
                                <p className="text-slate-400 text-xs">Use your admin credentials to access the platform</p>
                            </div>
                        </div>
                    </div>
                    
                    <button
                        onClick={() => navigate('/')}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                        Go to Login <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
                
                {/* Support */}
                <p className="text-center text-slate-500 text-sm">
                    Need help? Contact <a href="mailto:support@shipez.ai" className="text-teal-400 hover:underline">support@shipez.ai</a>
                </p>
            </div>
        </div>
    );
};

export default SignupSuccess;
